import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils, type InterfaceToken, toBigInt, ZERO } from '@morpho-org/dapp-core';
import { formatLongString, isDefined } from '@morpho-org/morpho-ts';
import { Avatar, NetworkAvatar, UserPositionIndicator } from '@morpho-org/ui-v6/client';
import { Card } from '@morpho-org/ui-v6/server';
import { formatters, getDigitsFromPriceAndAmount, getMarketNameParts, UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils';
import { Badge, Button, Icon, Separator, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { ArrowDiagonalUp } from '@repo/ui/icons';
import type { MarketCurator, TableMarketRow } from '@/components/common/MarketSection/TableMarket/config/TableMarket.types';
import { IconRewardHover } from '@/components/common/IconRewardHover';
import { InternalLink } from '@/components/common/InternalLink';
import MarketTrustedByCuratorStack from '@/components/common/MarketSection/TableMarket/components/MarketTrustedByCuratorStack';
import { SllBadge } from '@/components/common/SllBadge';
import { type Warning, WarningIcon } from '@/components/common/WarningIcon';
import { AVG_APY_DURATION_LABEL } from '@/constants/avgApy';
import { useWeb3Context } from '@/contexts/Web3Context';
import type { BaseMobileBorrowCardProps } from './MobileBorrowCard.types';
import { useBorrowCard } from './hooks/useBorrowCard';
const borrowRateLabel = `Borrow Rate (${AVG_APY_DURATION_LABEL.toLowerCase()})`;
const lendRateLabel = `Lend Rate (${AVG_APY_DURATION_LABEL.toLowerCase()})`;
interface TokenAmountValue {
  amount: bigint | null;
  valueUsd: bigint | null;
}
interface MobileMarketCardProps extends BaseMobileBorrowCardProps {
  chainId: number;
  totalLiquidity?: TokenAmountValue;
  totalBorrow?: TokenAmountValue;
  curators: MarketCurator[];
  hasUserPosition?: boolean;
  warnings: Warning[];
  /** Lend-side rate slice, shown alongside the borrow rate. */
  supply?: TableMarketRow['netSupplyApy'];
}
export default function MobileMarketCard(t0) {
  const $ = _c(100);
  const {
    href,
    chainId,
    collateral,
    loan,
    lltv,
    marketId,
    totalLiquidity,
    totalBorrow,
    netApy,
    apy,
    rewards,
    points,
    curators,
    hasUserPosition,
    warnings,
    supply
  } = t0;
  const {
    account,
    modal
  } = useWeb3Context();
  let t1;
  if ($[0] !== lltv || $[1] !== loan || $[2] !== marketId) {
    t1 = {
      marketId,
      loan,
      lltv
    };
    $[0] = lltv;
    $[1] = loan;
    $[2] = marketId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    formattedLltv,
    onBorrowClick
  } = useBorrowCard(t1);
  let t2;
  if ($[4] !== collateral.token || $[5] !== loan.token) {
    t2 = getMarketNameParts({
      loanAsset: loan.token,
      collateralAsset: collateral.token
    });
    $[4] = collateral.token;
    $[5] = loan.token;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    loanSymbol,
    collateralSymbol
  } = t2;
  let t3;
  if ($[7] !== formattedLltv) {
    t3 = <Badge variant="tertiary" size="sm">{formattedLltv}</Badge>;
    $[7] = formattedLltv;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const lltvBadge = t3;
  let t4;
  if ($[9] !== netApy) {
    t4 = formatters.percent.digits(2).unit("%").max(10).of(netApy, 18);
    $[9] = netApy;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const formattedNetApy = t4;
  let t5;
  if ($[11] !== supply?.netSupplyApy) {
    t5 = formatters.percent.digits(2).unit("%").max(10).of(supply?.netSupplyApy, 18);
    $[11] = supply?.netSupplyApy;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const formattedSupplyNetApy = t5;
  const t6 = totalBorrow?.amount;
  let t7;
  if ($[13] !== loan.token || $[14] !== t6) {
    t7 = formatTokenAmount(loan.token, t6);
    $[13] = loan.token;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const formattedBorrow = t7;
  const t8 = totalBorrow?.valueUsd;
  let t9;
  if ($[16] !== t8) {
    t9 = formatUsdValue(t8);
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const formattedBorrowUsd = t9;
  const t10 = totalLiquidity?.amount;
  let t11;
  if ($[18] !== loan.token || $[19] !== t10) {
    t11 = formatTokenAmount(loan.token, t10);
    $[18] = loan.token;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const formattedLiquidity = t11;
  const t12 = totalLiquidity?.valueUsd;
  let t13;
  if ($[21] !== t12) {
    t13 = formatUsdValue(t12);
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const formattedLiquidityUsd = t13;
  let t14;
  if ($[23] !== chainId) {
    t14 = InterfaceChainUtils.getMetadata(chainId);
    $[23] = chainId;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const networkName = t14.chain.name;
  let t15;
  if ($[25] !== loan.token.logoURI) {
    t15 = <Avatar aria-hidden={true} src={loan.token.logoURI} size="small" />;
    $[25] = loan.token.logoURI;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== loanSymbol) {
    t16 = <span className="text-foreground truncate text-xs leading-normal">{loanSymbol}</span>;
    $[27] = loanSymbol;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t15 || $[30] !== t16) {
    t17 = <span className="gap-2xs inline-flex min-w-0 items-center">{t15}{t16}</span>;
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== collateral.token || $[33] !== collateralSymbol || $[34] !== lltvBadge) {
    t18 = collateral.token ? <span className="gap-2xs inline-flex min-w-0 items-center"><span aria-hidden={true} className="bg-muted-foreground h-sm w-px shrink-0 rounded-full" /><span className="sr-only">backed by</span><span className="gap-2xs inline-flex min-w-0 items-center"><Avatar aria-hidden={true} src={collateral.token.logoURI} size="small" /><span className="text-foreground truncate text-xs leading-normal">{collateralSymbol}</span>{lltvBadge}</span></span> : lltvBadge;
    $[32] = collateral.token;
    $[33] = collateralSymbol;
    $[34] = lltvBadge;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== href) {
    t19 = href && <Icon className="text-foreground" icon={<ArrowDiagonalUp />} />;
    $[36] = href;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t17 || $[39] !== t18 || $[40] !== t19) {
    t20 = <div className="gap-2xs flex flex-wrap items-center">{t17}{t18}{t19}</div>;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const marketHeader = t20;
  let t21;
  if ($[42] !== href || $[43] !== marketHeader) {
    t21 = href ? <InternalLink href={href} prefetch={true}>{marketHeader}</InternalLink> : marketHeader;
    $[42] = href;
    $[43] = marketHeader;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== warnings) {
    t22 = <WarningIcon warnings={warnings} />;
    $[45] = warnings;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== hasUserPosition) {
    t23 = hasUserPosition && <UserPositionIndicator variant="market" />;
    $[47] = hasUserPosition;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== t21 || $[50] !== t22 || $[51] !== t23) {
    t24 = <div className="flex items-center"><div className="gap-xs flex items-center">{t21}{t22}{t23}</div></div>;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Separator className="my-xs" />;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      size: "small"
    };
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== chainId) {
    t27 = <NetworkAvatar chainId={chainId} srcAvatarProps={t26} />;
    $[55] = chainId;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== networkName) {
    t28 = <span className="text-3xs capitalize">{networkName}</span>;
    $[57] = networkName;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== t27 || $[60] !== t28) {
    t29 = <Row label="Network"><div className="gap-2xs flex items-center">{t27}{t28}</div></Row>;
    $[59] = t27;
    $[60] = t28;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== apy || $[63] !== formattedNetApy || $[64] !== netApy || $[65] !== points || $[66] !== rewards) {
    t30 = formattedNetApy && <Row label={borrowRateLabel}><IconRewardHover isMobileSupportEnabled={true} RewardsHoverCardProps={{
        netApy,
        apy,
        performanceFee: ZERO,
        rewards,
        variant: "market",
        points
      }}><span className="text-3xs text-foreground leading-normal">{formattedNetApy}<span className="sr-only"> {borrowRateLabel}</span></span></IconRewardHover></Row>;
    $[62] = apy;
    $[63] = formattedNetApy;
    $[64] = netApy;
    $[65] = points;
    $[66] = rewards;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== formattedSupplyNetApy || $[69] !== supply?.asset || $[70] !== supply?.netSupplyApy || $[71] !== supply?.points || $[72] !== supply?.rewards || $[73] !== supply?.supplyApy) {
    t31 = formattedSupplyNetApy && <Row label={lendRateLabel}><IconRewardHover isMobileSupportEnabled={true} RewardsHoverCardProps={{
        netApy: supply?.netSupplyApy,
        apy: supply?.supplyApy,
        performanceFee: ZERO,
        rewards: supply?.rewards,
        variant: "supply",
        points: supply?.points,
        asset: supply?.asset
      }}><span className="text-3xs text-foreground leading-normal">{formattedSupplyNetApy}<span className="sr-only"> {lendRateLabel}</span></span></IconRewardHover></Row>;
    $[68] = formattedSupplyNetApy;
    $[69] = supply?.asset;
    $[70] = supply?.netSupplyApy;
    $[71] = supply?.points;
    $[72] = supply?.rewards;
    $[73] = supply?.supplyApy;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== formattedBorrow || $[76] !== formattedBorrowUsd) {
    t32 = formattedBorrow && <Row label="Outstanding Loans"><div className="gap-xs flex items-center"><span className="text-3xs text-foreground leading-normal">{formattedBorrow}</span>{formattedBorrowUsd && <Badge variant="tertiary" size="sm">{formattedBorrowUsd}</Badge>}</div></Row>;
    $[75] = formattedBorrow;
    $[76] = formattedBorrowUsd;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  let t33;
  if ($[78] !== chainId || $[79] !== formattedLiquidity || $[80] !== formattedLiquidityUsd || $[81] !== marketId) {
    t33 = formattedLiquidity && <Row label="Total Liquidity"><div className="gap-xs flex items-center"><SllBadge chainId={chainId} className="size-5" marketId={marketId} source="market_explore" /><span className="text-3xs text-foreground leading-normal">{formattedLiquidity}</span>{formattedLiquidityUsd && <Badge variant="tertiary" size="sm">{formattedLiquidityUsd}</Badge>}</div></Row>;
    $[78] = chainId;
    $[79] = formattedLiquidity;
    $[80] = formattedLiquidityUsd;
    $[81] = marketId;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== curators || $[84] !== marketId) {
    t34 = <Row label="Trusted by">{curators.length > 0 ? <MarketTrustedByCuratorStack curators={curators} marketId={marketId as MarketId} avatarSize="small" maxTokensCount={5} align="right" /> : <Tooltip><TooltipTrigger asChild={true}><div className="text-muted-foreground flex cursor-help items-center"><Avatar size="small" /></div></TooltipTrigger><TooltipContent side="left">Unverified market: No vault curators deposit in this market.</TooltipContent></Tooltip>}</Row>;
    $[83] = curators;
    $[84] = marketId;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Separator className="my-xs" />;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== account.isConnected || $[88] !== modal || $[89] !== onBorrowClick) {
    t36 = <div className="gap-sm flex">{account.isConnected ? <Button onClick={onBorrowClick} className="flex-1 basis-1/3">Borrow</Button> : <Button onClick={() => modal.open()} className="flex-1 basis-1/2">Connect</Button>}</div>;
    $[87] = account.isConnected;
    $[88] = modal;
    $[89] = onBorrowClick;
    $[90] = t36;
  } else {
    t36 = $[90];
  }
  let t37;
  if ($[91] !== t24 || $[92] !== t29 || $[93] !== t30 || $[94] !== t31 || $[95] !== t32 || $[96] !== t33 || $[97] !== t34 || $[98] !== t36) {
    t37 = <Card data-testid="listing-card" padding="s" withBorder={true} role="article"><div className="gap-sm flex w-full flex-col">{t24}{t25}{t29}{t30}{t31}{t32}{t33}{t34}{t35}{t36}</div></Card>;
    $[91] = t24;
    $[92] = t29;
    $[93] = t30;
    $[94] = t31;
    $[95] = t32;
    $[96] = t33;
    $[97] = t34;
    $[98] = t36;
    $[99] = t37;
  } else {
    t37 = $[99];
  }
  return t37;
}
function Row(t0) {
  const $ = _c(5);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-3xs text-secondary-foreground">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className="flex min-h-5 w-full items-center justify-between">{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function formatTokenAmount(token: InterfaceToken | null | undefined, amount: bigint | null | undefined): string | undefined {
  if (!isDefined(amount) || !isDefined(token) || !isDefined(token.decimals)) {
    return undefined;
  }
  const amountBigInt = toBigInt(amount);
  const symbol = formatLongString(token.symbol || UNKNOWN_SYMBOL, 16);
  return formatters.short.digits(getDigitsFromPriceAndAmount(token.price, amountBigInt, token.decimals)).default('-').unit(symbol).of(amountBigInt, token.decimals);
}
function formatUsdValue(value: bigint | null | undefined): string | undefined {
  if (!isDefined(value)) return undefined;
  return formatters.short.unit('$').digits(2).of(value, 18);
}